<template>
  <Teleport to="body">
    <div
      v-if="isShown"
      ref="modalRoot"
      class="payment-hold-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="payment-hold-modal-title"
      aria-describedby="payment-hold-modal-body"
      tabindex="-1"
    >
      <div
        class="payment-hold-modal__backdrop"
        aria-hidden="true"
      />
      <div class="payment-hold-modal__panel">
        <FontAwesomeIcon
          :icon="appIcons.faLockKeyhole"
          class="payment-hold-modal__icon"
          aria-hidden="true"
        />
        <h2
          id="payment-hold-modal-title"
          class="payment-hold-modal__title"
        >
          {{ checkoutContent.paymentHoldTitle }}
        </h2>
        <p
          id="payment-hold-modal-body"
          class="payment-hold-modal__body"
        >
          {{ checkoutContent.paymentHoldMessage }}
        </p>
        <!-- Not disabled while checking: the focus trap needs something to focus. -->
        <button
          type="button"
          class="btn btn-alt payment-hold-modal__cta"
          :aria-busy="isChecking"
          @click="checkAgain"
        >
          <span
            v-if="isChecking"
            class="spinner-border spinner-border-sm me-2"
            aria-hidden="true"
          />
          {{ checkoutContent.checkAgainLabel }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import { useFocusTrap as useVueUseFocusTrap } from '@vueuse/integrations/useFocusTrap'

import useAppIcons from '@/composables/appIcons'
import { usePaymentHoldModal } from '@/composables/usePaymentHoldModal'

import { checkoutContent } from '@/content/checkout'

const BODY_OPEN_CLASS = 'payment-hold-open'

/**
 * Covers the page while another tab or device is paying for this cart, in place of disabling each
 * control. It can't be closed; it goes when the hold ends. See iteration 13 of the #472 plan.
 */
export default defineComponent({
  name: 'PaymentHoldModal',
  setup() {
    const { isShown, isChecking, checkAgain } = usePaymentHoldModal()

    const modalRoot = ref<HTMLElement | null>(null)
    // Bypasses the shared `useFocusTrap`, whose watcher isn't immediate: this modal can be showing
    // when the page loads. Nothing deactivates the trap but the hold ending.
    const { activate, deactivate } = useVueUseFocusTrap(modalRoot, {
      escapeDeactivates: false,
      clickOutsideDeactivates: false,
      allowOutsideClick: false,
      fallbackFocus: () => modalRoot.value as HTMLElement
    })

    watch(
      isShown,
      async (shown) => {
        document.body.classList.toggle(BODY_OPEN_CLASS, shown)

        if (shown) {
          await nextTick()
          activate()
        } else {
          deactivate()
        }
      },
      { immediate: true }
    )

    onBeforeUnmount(() => document.body.classList.remove(BODY_OPEN_CLASS))

    return { ...useAppIcons(), checkoutContent, modalRoot, isShown, isChecking, checkAgain }
  }
})
</script>

<style scoped lang="scss">
// Above the cart flyout and Bootstrap's modal and tooltip layers (1040–1080), below
// vue-toastification's 9999, so a refused change's toast still shows over it.
.payment-hold-modal {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
  }

  &__panel {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    width: 100%;
    max-width: 30rem;
    padding: 2rem 1.5rem;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    text-align: center;

    @include media-breakpoint-up(md) {
      padding: 2.5rem;
    }
  }

  &__icon {
    font-size: 2rem;
  }

  &__title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  &__body,
  &__note {
    margin: 0;
  }

  &__note {
    font-size: 0.875rem;
    color: var(--bs-gray-700);
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 12rem;
    margin-top: 0.5rem;
  }
}
</style>
